import React, { useState, useEffect } from 'react';
import { FileText, MapPin, X, UserCircle, Eye, Check, Clock, Calendar, IndianRupee, Camera, Mail, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, collection, getDocs, updateDoc, query, orderBy } from 'firebase/firestore';
import { toast } from 'react-toastify';
import './PhotographerRequests.css';
import DashboardSidebar from './DashboardSidebar';


// Local Storage Helper
const LocalStorageHelper = {
    saveLoginData: (userId, email, name, userType = 'photographer') => {
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('userId', userId);
        localStorage.setItem('userEmail', email);
        localStorage.setItem('userName', name);
        localStorage.setItem('userType', userType);
    },
    isLoggedIn: () => {
        return localStorage.getItem('isLoggedIn') === 'true';
    },
    getLoginData: () => {
        return {
            userId: localStorage.getItem('userId'),
            email: localStorage.getItem('userEmail'),
            name: localStorage.getItem('userName'),
            userType: localStorage.getItem('userType'),
        };
    },
    clearLoginData: () => {
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem('userId');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userName');
        localStorage.removeItem('userType');
    }
};


const PhotographerRequests = () => {
    const navigate = useNavigate();
    const [sidebarVisible, setSidebarVisible] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);


    // Authentication state
    const [currentUser, setCurrentUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [userName, setUserName] = useState('Photographer');
    const [profilePhoto, setProfilePhoto] = useState(null);


    // Requests state
    const [requests, setRequests] = useState([]);
    const [loadingRequests, setLoadingRequests] = useState(true);
    const [filterStatus, setFilterStatus] = useState('all'); // all, pending, accepted, rejected


    // Request details modal state
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showRequestModal, setShowRequestModal] = useState(false);


    // Generate avatar URL helper
    const generateAvatarUrl = (userData, user) => {
        const avatarSources = [
            userData?.profileImage,
            userData?.avatarUrl,
            userData?.avatar,
            user?.photoURL
        ];


        const validAvatar = avatarSources.find(url => url && url.trim() !== '' && url !== 'undefined');


        if (validAvatar) {
            return validAvatar;
        }


        const name = userData?.name || user?.displayName || "User";
        return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=6366f1&color=fff&size=128&bold=true&font-size=0.6`;
    };


    // Authentication check
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                setCurrentUser(user);
                await loadUserData(user.uid);
                await fetchRequests(user.uid);
            } else {
                navigate('/Login');
            }
        });


        return () => unsubscribe();
    }, [navigate]);


    // Load photographer data
    const loadUserData = async (userId) => {
        try {
            const userDoc = await getDoc(doc(db, 'photographers', userId));
            if (userDoc.exists()) {
                const data = userDoc.data();
                setUserData(data);
                setUserName(data.name || data.displayName || 'Photographer');
                setProfilePhoto(data.profileImage || data.avatar || null);
            } else {
                // Fallback to auth user data
                if (auth.currentUser) {
                    setUserName(auth.currentUser.displayName || 'Photographer');
                }
            }
        } catch (error) {
            console.error('Error loading user data:', error);
            if (auth.currentUser) {
                setUserName(auth.currentUser.displayName || 'Photographer');
            }
        }
    };


    // Fetch requests for this photographer
    const fetchRequests = async (photographerId) => {
        try {
            setLoadingRequests(true);
            console.log('Fetching requests for photographer:', photographerId);


            const requestsRef = collection(db, 'photographers', photographerId, 'requests');
            const q = query(requestsRef, orderBy('createdAt', 'desc'));
            const requestsSnapshot = await getDocs(q);


            const requestsList = [];
            for (const requestDoc of requestsSnapshot.docs) {
                const requestData = requestDoc.data();


                // Fetch user details for each request
                try {
                    const userDoc = await getDoc(doc(db, 'users', requestData.userId));
                    const userData = userDoc.exists() ? userDoc.data() : {};


                    requestsList.push({
                        id: requestDoc.id,
                        ...requestData,
                        userDetails: {
                            name: userData.name || userData.displayName || 'Unknown User',
                            email: userData.email || requestData.userEmail || 'No email',
                            phone: userData.phone || 'No phone',
                            profileImage: userData.profileImage || userData.avatar || null
                        }
                    });
                } catch (userError) {
                    console.error('Error fetching user details:', userError);
                    requestsList.push({
                        id: requestDoc.id,
                        ...requestData,
                        userDetails: {
                            name: 'Unknown User',
                            email: requestData.userEmail || 'No email',
                            phone: 'No phone',
                            profileImage: null
                        }
                    });
                }
            }


            console.log('Fetched requests:', requestsList);
            setRequests(requestsList);
        } catch (error) {
            console.error('Error fetching requests:', error);
            toast.error('Failed to load requests');
        } finally {
            setLoadingRequests(false);
        }
    };


    // Accept a request
    const handleAcceptRequest = async (request, event) => {
        event.stopPropagation();
        try {
            console.log('Accepting request:', request);


            // Update the request status in Firestore
            await updateDoc(
                doc(db, 'photographers', currentUser.uid, 'requests', request.id),
                {
                    status: 'accepted',
                    acceptedAt: new Date(),
                    updatedAt: new Date()
                }
            );


            // Update local state
            setRequests(prev => prev.map(r =>
                r.id === request.id
                    ? { ...r, status: 'accepted', acceptedAt: new Date() }
                    : r
            ));


            toast.success('Request accepted successfully!');
        } catch (error) {
            console.error('Error accepting request:', error);
            toast.error('Failed to accept request');
        }
    };


    // Reject a request
    const handleRejectRequest = async (request, event) => {
        event.stopPropagation();
        try {
            console.log('Rejecting request:', request);


            // Update the request status in Firestore
            await updateDoc(
                doc(db, 'photographers', currentUser.uid, 'requests', request.id),
                {
                    status: 'rejected',
                    rejectedAt: new Date(),
                    updatedAt: new Date()
                }
            );


            // Update local state
            setRequests(prev => prev.map(r =>
                r.id === request.id
                    ? { ...r, status: 'rejected', rejectedAt: new Date() }
                    : r
            ));


            toast.success('Request rejected');
        } catch (error) {
            console.error('Error rejecting request:', error);
            toast.error('Failed to reject request');
        }
    };


    // Handle view request details
    const handleViewRequest = (request, event) => {
        event.stopPropagation();
        setSelectedRequest(request);
        setShowRequestModal(true);
    };


    // Close request details modal
    const closeRequestModal = () => {
        setShowRequestModal(false);
        setSelectedRequest(null);
    };


    // Filter requests based on status
    const filteredRequests = requests.filter(request => {
        if (filterStatus === 'all') return true;
        return request.status === filterStatus || (!request.status && filterStatus === 'pending');
    });


    // Time ago helper function
    const timeAgo = (timestamp) => {
        if (!timestamp) return 'Recently';


        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const now = new Date();
        const seconds = Math.floor((now - date) / 1000);


        if (seconds < 60) return 'Just now';
        if (seconds < 3600) {
            const minutes = Math.floor(seconds / 60);
            return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
        }
        if (seconds < 86400) {
            const hours = Math.floor(seconds / 3600);
            return `${hours} hour${hours > 1 ? 's' : ''} ago`;
        }
        if (seconds < 604800) {
            const days = Math.floor(seconds / 86400);
            return `${days} day${days > 1 ? 's' : ''} ago`;
        }


        return date.toLocaleDateString();
    };


    // Format date for display - UPDATED to handle both preferredDate and eventDate
    const formatDate = (request) => {
        // Try preferredDate first (as sent from the form), then eventDate as fallback
        const timestamp = request.preferredDate || request.eventDate;
        
        if (!timestamp) return 'Date not specified';
        
        try {
            const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
            return date.toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'Date not available';
        }
    };


    // Format budget display - UPDATED to handle custom budget strings
    const formatBudget = (budget) => {
        if (!budget) return 'Budget not specified';
        
        // If it's already a formatted string (custom input), return as is with rupee symbol
        if (typeof budget === 'string') {
            // Check if it already has currency symbol
            if (budget.includes('₹') || budget.includes('Rs')) {
                return budget;
            }
            // Add rupee symbol if it's a number or range
            return `₹${budget}`;
        }
        
        // Legacy predefined budget ranges (if still used)
        const budgetMap = {
            'under-25k': 'Under ₹25,000',
            '25k-50k': '₹25,000 - ₹50,000',
            '50k-1l': '₹50,000 - ₹1,00,000',
            '1l-2l': '₹1,00,000 - ₹2,00,000',
            'above-2l': 'Above ₹2,00,000'
        };
        
        return budgetMap[budget] || `₹${budget}`;
    };


    // Format time of day
    const formatTimeOfDay = (timeOfDay) => {
        if (!timeOfDay) return 'Time not specified';
        
        const timeMap = {
            'morning': 'Morning (6 AM - 12 PM)',
            'afternoon': 'Afternoon (12 PM - 6 PM)',
            'evening': 'Evening (6 PM - 10 PM)',
            'flexible': 'Flexible'
        };
        return timeMap[timeOfDay] || timeOfDay;
    };


    // Format type of shoot - UPDATED to handle dynamic shoot types
    const formatTypeOfShoot = (typeOfShoot) => {
        if (!typeOfShoot) return 'Type not specified';
        
        // Remove hyphens and convert to proper case
        const formatted = typeOfShoot
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        
        // Add "Photography" suffix if not already present
        if (!formatted.toLowerCase().includes('photography')) {
            return `${formatted} Photography`;
        }
        
        return formatted;
    };


    // Get status color
    const getStatusColor = (status) => {
        switch (status) {
            case 'accepted':
                return '#16a34a';
            case 'rejected':
                return '#dc2626';
            default:
                return '#b45309';
        }
    };


    // Get status text
    const getStatusText = (status) => {
        switch (status) {
            case 'accepted':
                return 'Accepted';
            case 'rejected':
                return 'Rejected';
            default:
                return 'Pending';
        }
    };


    // Handle logout
    const handleLogout = async () => {
        try {
            await signOut(auth);
            LocalStorageHelper.clearLoginData();
            navigate('/Login');
        } catch (error) {
            console.error('Error logging out:', error);
        }
    };


    // Handle home navigation
    const handleHomeNavigation = () => {
        navigate('/LandingPage');
        closeSidebar();
    };


    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (!mobile && !sidebarVisible) {
                setSidebarVisible(true);
            }
        };


        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [sidebarVisible]);


    const toggleSidebar = () => {
        setSidebarVisible(!sidebarVisible);
    };


    const closeSidebar = () => {
        if (isMobile) {
            setSidebarVisible(false);
        }
    };


    return (
        <div className="photographer-requests-container">
            <DashboardSidebar
                activeItem="requests"
                visible={sidebarVisible}
                isMobile={isMobile}
                onToggle={toggleSidebar}
                onClose={closeSidebar}
                userName={userName}
                profilePhoto={profilePhoto}
                onHome={handleHomeNavigation}
                onLogout={handleLogout}
            />

            {/* Main Content */}
            <div className={`requests-main-content ${!sidebarVisible ? 'expanded' : ''}`}>
                {/* Page Title */}
                <h1 className="requests-page-title">Requests</h1>


                {/* Content Section */}
                <div className="requests-content-section">
                    {/* Filter Tabs */}
                    <div className="filter-tabs">
                        <button
                            className={filterStatus === 'all' ? 'active' : ''}
                            onClick={() => setFilterStatus('all')}
                        >
                            All Requests
                        </button>
                        <button
                            className={filterStatus === 'pending' ? 'active' : ''}
                            onClick={() => setFilterStatus('pending')}
                        >
                            Pending
                        </button>
                        <button
                            className={filterStatus === 'accepted' ? 'active' : ''}
                            onClick={() => setFilterStatus('accepted')}
                        >
                            Accepted
                        </button>
                        <button
                            className={filterStatus === 'rejected' ? 'active' : ''}
                            onClick={() => setFilterStatus('rejected')}
                        >
                            Rejected
                        </button>
                    </div>


                    {/* Requests Grid */}
                    <div className="requests-grid">
                        {loadingRequests ? (
                            <div className="loading-container">
                                <div className="loading-spinner"></div>
                                <p>Loading requests...</p>
                            </div>
                        ) : filteredRequests.length === 0 ? (
                            <div className="no-requests">
                                <FileText size={48} />
                                <h3>No Requests Found</h3>
                                <p>
                                    {filterStatus === 'all'
                                        ? "You haven't received any photography requests yet."
                                        : `No ${filterStatus} requests found.`
                                    }
                                </p>
                            </div>
                        ) : (
                            filteredRequests.map((request) => (
                                <div key={request.id} className="request-card">
                                    <div className="request-header">
                                        <div className="client-info">
                                            <div className="client-avatar">
                                                {request.userDetails.profileImage ? (
                                                    <img
                                                        src={request.userDetails.profileImage}
                                                        alt={request.userDetails.name}
                                                        loading="lazy"
                                                        decoding="async"
                                                    />
                                                ) : (
                                                    <UserCircle size={24} />
                                                )}
                                            </div>
                                            <div className="client-details">
                                                <h4>{request.userDetails.name}</h4>
                                                <p>{request.userDetails.email}</p>
                                            </div>
                                        </div>
                                        <div
                                            className="status-badge"
                                            style={{ backgroundColor: getStatusColor(request.status) }}
                                        >
                                            {getStatusText(request.status)}
                                        </div>
                                    </div>


                                    <div className="request-details">
                                        <div className="detail-item">
                                            <Calendar size={16} />
                                            <span>{formatDate(request)}</span>
                                        </div>
                                        <div className="detail-item">
                                            <Clock size={16} />
                                            <span>{formatTimeOfDay(request.timeOfDay)}</span>
                                        </div>
                                        <div className="detail-item">
                                            <MapPin size={16} />
                                            <span>{request.location || 'Location not specified'}</span>
                                        </div>
                                        <div className="detail-item">
                                            <Camera size={16} />
                                            <span>{formatTypeOfShoot(request.typeOfShoot)}</span>
                                        </div>
                                        <div className="detail-item">
    <IndianRupee size={16} />
    <span>{formatBudget(request.budget)}</span>
</div>

                                    </div>


                                    {request.message && (
                                        <div className="request-message">
                                            <p>"{request.message}"</p>
                                        </div>
                                    )}


                                    <div className="request-footer">
                                        <div className="request-time">
                                            {timeAgo(request.createdAt)}
                                        </div>
                                        <div className="request-actions">
                                            <button
                                                className="view-btn"
                                                onClick={(e) => handleViewRequest(request, e)}
                                            >
                                                <Eye size={14} />
                                                View
                                            </button>
                                            {(!request.status || request.status === 'pending') && (
                                                <>
                                                    <button
                                                        className="accept-btn"
                                                        onClick={(e) => handleAcceptRequest(request, e)}
                                                    >
                                                        <Check size={14} />
                                                        Accept
                                                    </button>
                                                    <button
                                                        className="reject-btn"
                                                        onClick={(e) => handleRejectRequest(request, e)}
                                                    >
                                                        <X size={14} />
                                                        Reject
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>


            {/* Request Details Modal */}
            {showRequestModal && selectedRequest && (
                <div className="modal-overlay" onClick={closeRequestModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Request Details</h3>
                            <button className="close-modal" onClick={closeRequestModal}>
                                <X size={20} />
                            </button>
                        </div>


                        <div className="modal-body">
                            {/* Client Information */}
                            <div className="client-section">
                                <h4>Client Information</h4>
                                <div className="client-info-detailed">
                                    <div className="client-avatar-large">
                                        {selectedRequest.userDetails.profileImage ? (
                                            <img
                                                src={selectedRequest.userDetails.profileImage}
                                                alt={selectedRequest.userDetails.name}
                                            />
                                        ) : (
                                            <UserCircle size={40} />
                                        )}
                                    </div>
                                    <div className="client-contact">
                                        <h5>{selectedRequest.userDetails.name}</h5>
                                        <div className="contact-item">
                                            <Mail size={16} />
                                            <span>{selectedRequest.userDetails.email}</span>
                                        </div>
                                        {selectedRequest.userDetails.phone && selectedRequest.userDetails.phone !== 'No phone' && (
                                            <div className="contact-item">
                                                <Phone size={16} />
                                                <span>{selectedRequest.userDetails.phone}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>


                            {/* Request Details */}
                            <div className="request-section">
                                <h4>Request Details</h4>
                                <div className="request-details-grid">
                                    <div className="detail-row">
                                        <label>Event Date:</label>
                                        <span>{formatDate(selectedRequest)}</span>
                                    </div>
                                    <div className="detail-row">
                                        <label>Time:</label>
                                        <span>{formatTimeOfDay(selectedRequest.timeOfDay)}</span>
                                    </div>
                                    <div className="detail-row">
                                        <label>Location:</label>
                                        <span>{selectedRequest.location || 'Location not specified'}</span>
                                    </div>
                                    <div className="detail-row">
                                        <label>Type of Shoot:</label>
                                        <span>{formatTypeOfShoot(selectedRequest.typeOfShoot)}</span>
                                    </div>
                                    <div className="detail-row">
                                        <label>Budget:</label>
                                        <span>{formatBudget(selectedRequest.budget)}</span>
                                    </div>
                                    <div className="detail-row">
                                        <label>Status:</label>
                                        <span
                                            style={{
                                                color: getStatusColor(selectedRequest.status),
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {getStatusText(selectedRequest.status)}
                                        </span>
                                    </div>
                                    <div className="detail-row">
                                        <label>Requested:</label>
                                        <span>{timeAgo(selectedRequest.createdAt)}</span>
                                    </div>
                                </div>


                                {/* Message Section */}
                                {selectedRequest.message && (
                                    <div className="message-section">
                                        <label>Message from Client:</label>
                                        <div className="message-content">
                                            <p>{selectedRequest.message}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>


                        <div className="modal-footer">
                            <div className="modal-actions">
                                {(!selectedRequest.status || selectedRequest.status === 'pending') && (
                                    <>
                                        <button
                                            className="accept-btn"
                                            onClick={(e) => {
                                                handleAcceptRequest(selectedRequest, e);
                                                closeRequestModal();
                                            }}
                                        >
                                            <Check size={16} />
                                            Accept Request
                                        </button>
                                        <button
                                            className="reject-btn"
                                            onClick={(e) => {
                                                handleRejectRequest(selectedRequest, e);
                                                closeRequestModal();
                                            }}
                                        >
                                            <X size={16} />
                                            Reject Request
                                        </button>
                                    </>
                                )}
                            </div>
                            <button className="close-btn" onClick={closeRequestModal}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};


export default PhotographerRequests;
